// Mini progress bar: a 4-px track with a tone-coloured fill.
//
// `percentage` is clamped to [0, 100] and rounded for both rendering and
// the aria-valuenow attribute, so a caller passing 25.7 gets a 26% bar
// and an assistive-tech announcement of "26".

import { clsx } from "clsx";

export type ProgressTone = "success" | "warning" | "danger" | "brand";

export interface ProgressBarProps {
  percentage: number;
  tone?: ProgressTone;
  /** Optional sr-only label for screen readers; if omitted, the bar
   *  announces just the percentage via aria-valuenow. */
  label?: string;
}

const TONE_FILL: Record<ProgressTone, string> = {
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  brand: "bg-brand-500",
};

export default function ProgressBar({
  percentage,
  tone = "brand",
  label,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, Math.round(percentage)));
  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
    >
      <div
        data-testid="progress-bar-fill"
        className={clsx("h-full rounded-full transition-[width]", TONE_FILL[tone])}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
